import { findIndex } from "lodash";
import { createCachedSelector } from "re-reselect";
import { createSelector } from "reselect";

import { getOrderedActions } from "pages/Editors/ApiEditor/ApiCanvas/utils";
import type { ApiScope } from "utils/dataTree/scope";

import slice from "./slice";

export const selectAllApis = createSelector(
  slice.selector,
  (state) => state.entities,
);

export const selectAllApiNames = createSelector(slice.selector, (state) => {
  return Object.values(state.entities).map((api) => api?.actions?.name ?? "");
});

export const selectApiById = createCachedSelector(
  slice.selector,
  (state: unknown, apiId: string | undefined) => apiId,
  (slice, apiId) => (apiId ? slice.entities[apiId] : undefined),
)((state, apiId) => apiId ?? "undefined");

export const selectApiMeta = createSelector(
  slice.selector,
  (state) => state.meta,
);

export const selectApiMetaById = createCachedSelector(
  slice.selector,
  (state: unknown, apiId: string | undefined) => apiId,
  (state, apiId) => (apiId ? state.meta[apiId] : undefined),
)((state: unknown, apiId: string | undefined) => apiId ?? "undefined");

export const selectActionById = createCachedSelector(
  slice.selector,
  (
    state: unknown,
    apiId: string | undefined,
    actionId: string | undefined,
  ) => ({ apiId, actionId }),
  (slice, { apiId, actionId }) =>
    apiId && actionId
      ? slice.entities[apiId]?.actions?.actions?.[actionId]
      : undefined,
)((state, apiId, actionId) => `${apiId}_${actionId}`);

export const selectApiLoadingById = createCachedSelector(
  slice.selector,
  (state: unknown, apiId: string) => apiId,
  (state, apiId) =>
    Boolean(state.loading[apiId]) ||
    Boolean((state.meta[apiId]?.concurrentRuns ?? 0) > 0) ||
    Boolean(state.meta[apiId]?.waitingForEvaluationSince),
)((state, apiId) => apiId);

export const selectApiRunCancelledById = createCachedSelector(
  slice.selector,
  (state: unknown, apiId: string) => apiId,
  (state, apiId) => Boolean(state.meta[apiId]?.cancelled),
)((state, apiId) => apiId);

export const selectApiExtractedBindings = createSelector(
  slice.selector,
  (state) => {
    const idToName = Object.fromEntries(
      Object.values(state.entities).map((api) => [api.id, api?.actions?.name]),
    );
    return Object.fromEntries(
      Object.entries(state.meta).map(([id, api]) => {
        return [
          id,
          {
            id,
            name: idToName[id],
            bindings: api.extractedBindings,
          },
        ];
      }),
    );
  },
);

const selectOrderedActionsByApiId = createCachedSelector(selectApiById, (api) =>
  getOrderedActions(api?.actions),
)((state, apiId: string | undefined) => apiId ?? "undefined");

export const selectUserAccessibleScope = createCachedSelector(
  selectApiById,
  selectOrderedActionsByApiId,
  (state: unknown, apiId: string | undefined, actionId: string | undefined) =>
    actionId,
  (api, orderedActions, actionId) => {
    if (!api || !actionId) {
      return;
    }

    const currentActionIdx = findIndex(
      orderedActions,
      (action) => action.id === actionId,
    );

    return {
      apiName: api?.actions?.name,
      previousV1ActionNames: orderedActions
        .slice(0, currentActionIdx)
        .map((action) => action.name),
    } as ApiScope;
  },
)(
  (state, apiId: string | undefined, actionId?: string | undefined) =>
    `${apiId}_${actionId}`,
);

export const selectTestProfile = createCachedSelector(
  slice.selector,
  (state: unknown, apiId: string | undefined) => apiId,
  (slice, apiId) => (apiId ? slice.meta[apiId]?.testProfile : undefined),
)((state, apiId) => apiId ?? "undefined");
